/**
 * The International standard's sidebar (ADR 0095): the workspace-shaped console the founder
 * photographed on 2026-09-13.
 *
 * A workspace switcher at the top with the door to a second workspace inside it, a Create button
 * under it, the four working areas with everything else under More, the TEAM group, and Admin
 * Settings at the foot. `flowza_sign` keeps the nav it has always had, byte for byte — the shell
 * decides which of the two to draw, and this file is reached only under `international`.
 *
 * ## Why the switcher is here and not in the header
 *
 * The FlowZa Sign shell hides its team switcher until there are two teams to switch between,
 * because a disabled control invents a concept the reader does not have yet. Under this standard
 * the switcher IS the door to the second team — "invite people, go to settings, and switch between
 * workspaces" is what the screenshot's own tooltip says of it — so it is always drawn, and its menu
 * carries the create, invite and settings items that make it worth opening with one team.
 *
 * ## Creating a workspace turns teams on, and the dialog says so before the click
 *
 * `POST /v1/workspaces` is refused with 403 `workspaces_disabled` while the organization does not
 * use teams (20260913000200). A dialog that let somebody type a name and then answered "turn teams
 * on somewhere else" would be `ux.md` §12's dead end wearing a form. So when the switch is off the
 * dialog says that creating a workspace turns teams on, and does it through the same
 * `PATCH /v1/organization` the Teams section in Settings uses. Both writes need an administrator,
 * and a refusal is shown in the dialog with the API's own sentence rather than swallowed.
 *
 * ## Why the menus are hand-rolled
 *
 * `@flowza/ui`'s `Menu` is a fixed 32 px icon button with a list under it. The switcher is a wide
 * button carrying a name, and Create is the sidebar's primary action; both keep the same keyboard
 * contract (Escape closes and returns focus to the trigger, click-away closes, `aria-expanded` on
 * the trigger, `role="menu"` on the list) the Start band's menu keeps, for the same reason.
 */
import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { Avatar, Button, Dialog, Field, Icon } from '@flowza/ui'
import { ApiError, api } from '../../api/client'
import { describeUnlessNotDeployed } from './apiErrors'
import { createWorkspace } from '../../api/workspaces'
import { AreaLink, SHELF_AREAS, TEAM_AREAS, type Area, type Workspace } from './nav'
import { UNKNOWN_OFFERING, mayInvite, offersAny, type Offering } from '../../auth/permissions'
import { ADMIN_CONSOLE_KEYS } from './admin/orgSections'
import { destinations } from './StartBand'

/** The four areas a sender works in, in the order the screenshot lists them. */
export const INTERNATIONAL_AREAS: readonly Area[] = [
  {
    to: '/app',
    label: 'My Documents',
    icon: 'file-text',
    group: 'workspace',
    end: true,
    ready: true,
  },
  {
    to: '/app/templates',
    label: 'My Templates',
    icon: 'template',
    group: 'workspace',
    ready: true,
    // `GET /v1/templates` is `templates.create`, which a viewer and a billing admin do not hold.
    requires: ['templates.create'],
  },
  { to: '/app/reports', label: 'Reports', icon: 'chart', group: 'workspace', ready: true },
  { to: '/app/contacts', label: 'Contacts', icon: 'users', group: 'workspace', ready: true },
]

/**
 * Everything under More. Nothing is dropped from the product by this standard — the inbox, settings
 * and the two developer areas are all still one click away; they sit behind a disclosure because
 * the screenshot's sidebar is four rows and a More, not eleven rows.
 *
 * The two SHELVES were here and are not any more (founder, 2026-09-16): the Inbox's Archived and
 * Deleted tabs are the same envelopes with the same restore, so these were a second door to a room
 * the reader is already in. They keep their routes and their ⌘K entries (`SHELF_AREAS` in
 * `nav.tsx`), and the library's own shelf rail still reaches them from Documents.
 */
export const MORE_AREAS: readonly Area[] = [
  { to: '/app/inbox', label: 'Inbox', icon: 'inbox', group: 'workspace', ready: true },
  { to: '/app/settings', label: 'Settings', icon: 'sliders', group: 'organization', ready: true },
  // Offered by permission (ADR 0196), with the same keys the FlowZa Sign sidebar names for them.
  {
    to: '/app/developer',
    label: 'Developer',
    icon: 'code',
    group: 'organization',
    ready: true,
    requires: ['api_keys.manage'],
  },
  {
    to: '/app/connect',
    label: 'Connect',
    icon: 'activity',
    group: 'organization',
    ready: true,
    requires: ['webhooks.manage'],
  },
]

/**
 * Pinned at the foot of the sidebar, where the screenshot has it — for somebody who holds at least
 * one key the console's screens read under (ADR 0196). A viewer sees no foot entry at all.
 */
export const ADMIN_AREA: Area = {
  to: '/app/admin',
  label: 'Admin Settings',
  icon: 'shield',
  group: 'organization',
  ready: true,
  requires: ADMIN_CONSOLE_KEYS,
}

/**
 * What the ⌘K palette offers under this standard: every destination the sidebar has, flat — less
 * the ones this caller may not open. `offering` defaults to "offer everything" for a caller that
 * has not asked, which is what a shell with no permissions provider above it gets.
 */
export function internationalPaletteAreas(
  teamsVisible: boolean,
  offering: Offering = UNKNOWN_OFFERING,
): readonly Area[] {
  return [
    ...INTERNATIONAL_AREAS,
    ...MORE_AREAS,
    // Not in the sidebar, still in the palette: see `SHELF_AREAS`.
    ...SHELF_AREAS,
    ...(teamsVisible ? TEAM_AREAS : []),
    ADMIN_AREA,
  ].filter((area) => offersAny(offering, area.requires))
}

export function InternationalNav({
  workspaces,
  activeWorkspaceId,
  workspacesEnabled,
  teamsVisible,
  onSwitch,
  onCreated,
  onNavigate,
  offering = UNKNOWN_OFFERING,
}: {
  readonly workspaces: readonly Workspace[]
  readonly activeWorkspaceId: string
  /** What the caller may open (ADR 0196); everything, when nobody has asked. */
  readonly offering?: Offering
  /** Whether the organization uses teams (20260913000200). Off, creating a workspace turns it on. */
  readonly workspacesEnabled: boolean
  readonly teamsVisible: boolean
  readonly onSwitch: (workspaceId: string) => void
  readonly onCreated: (workspace: Workspace) => void
  /** A navigation closes the drawer on a phone. */
  readonly onNavigate: () => void
}) {
  const [creating, setCreating] = useState(false)
  const location = useLocation()
  // More opens by itself when the reader is already somewhere inside it — a collapsed disclosure
  // hiding the current page would leave the rail with nothing highlighted.
  const [moreOpen, setMoreOpen] = useState(() =>
    MORE_AREAS.some((area) => location.pathname.startsWith(area.to)),
  )
  const moreId = useId()

  const active = workspaces.find((w) => w.id === activeWorkspaceId) ?? workspaces[0] ?? null

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-col gap-2 px-3 pt-3">
        <WorkspaceMenu
          workspaces={workspaces}
          active={active}
          onSwitch={onSwitch}
          onCreate={() => setCreating(true)}
          onNavigate={onNavigate}
          canInvite={mayInvite(offering)}
        />
        <CreateMenu onNavigate={onNavigate} />
      </div>

      <nav aria-label="Main" className="flex-1 overflow-y-auto px-3 pb-4 pt-3">
        <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
          {INTERNATIONAL_AREAS.filter((area) => offersAny(offering, area.requires)).map((area) => (
            <li key={area.to}>
              <AreaLink area={area} onNavigate={onNavigate} />
            </li>
          ))}
          <li>
            <button
              type="button"
              aria-expanded={moreOpen}
              aria-controls={moreId}
              onClick={() => setMoreOpen((open) => !open)}
              className="h-row text-text-muted hover:bg-surface-raised hover:text-text-primary flex w-full items-center gap-2.5 rounded-md pl-3.5 pr-2.5 text-sm font-medium"
            >
              <Icon name="more-horizontal" size={16} />
              <span className="flex-1 text-left">More</span>
              <Icon
                name="chevron-down"
                size={14}
                className={`transition-transform ${moreOpen ? 'rotate-180' : ''}`}
              />
            </button>
          </li>
        </ul>
        {moreOpen ? (
          <ul id={moreId} className="m-0 mt-0.5 flex list-none flex-col gap-0.5 p-0 pl-3">
            {MORE_AREAS.filter((area) => offersAny(offering, area.requires)).map((area) => (
              <li key={area.to}>
                <AreaLink area={area} onNavigate={onNavigate} />
              </li>
            ))}
          </ul>
        ) : null}

        {/*
          The same rule as the FlowZa Sign nav (`Shell.tsx`): the TEAM group appears when the
          organization uses teams and the caller is on one, and an empty group heading is worse
          than none.
        */}
        {teamsVisible && workspaces.length > 0 ? (
          <div className="mt-4">
            <p className="text-text-muted m-0 mb-1 px-2.5 text-xs font-medium uppercase tracking-wide">
              Team
            </p>
            <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
              {TEAM_AREAS.filter((area) => offersAny(offering, area.requires)).map((area) => (
                <li key={area.to}>
                  <AreaLink area={area} onNavigate={onNavigate} />
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </nav>

      {offersAny(offering, ADMIN_AREA.requires) ? (
        <div className="border-border-subtle border-t px-3 py-2">
          <AreaLink area={ADMIN_AREA} onNavigate={onNavigate} />
        </div>
      ) : null}

      <CreateWorkspaceDialog
        open={creating}
        teamsOn={workspacesEnabled}
        onClose={() => setCreating(false)}
        onCreated={(workspace) => {
          setCreating(false)
          onCreated(workspace)
        }}
      />
    </div>
  )
}

/* ----------------------------------------------------------------------- the dropdowns ------ */

/**
 * A trigger and a menu with the Start band's keyboard contract. Escape closes and puts focus BACK
 * on the trigger — the obligation nobody notices is missing until a keyboard user closes a menu
 * and finds themselves at the top of the page.
 */
function Dropdown({
  label,
  trigger,
  children,
}: {
  readonly label: string
  readonly trigger: (state: { readonly open: boolean; readonly toggle: () => void }) => ReactNode
  readonly children: (close: () => void) => ReactNode
}) {
  const [open, setOpen] = useState(false)
  const root = useRef<HTMLDivElement | null>(null)
  const menuId = useId()

  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setOpen(false)
      root.current?.querySelector<HTMLElement>('[aria-haspopup="menu"]')?.focus()
    }
    const onClick = (event: MouseEvent) => {
      if (root.current?.contains(event.target as Node) === true) return
      setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onClick)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onClick)
    }
  }, [open])

  const close = useCallback(() => setOpen(false), [])
  const toggle = useCallback(() => setOpen((o) => !o), [])

  return (
    <div ref={root} className="relative">
      {trigger({ open, toggle })}
      {open ? (
        <div
          id={menuId}
          role="menu"
          aria-label={label}
          className="border-border-strong bg-surface-raised shadow-overlay absolute inset-x-0 z-40 mt-1 rounded-md border p-1.5"
        >
          {children(close)}
        </div>
      ) : null}
    </div>
  )
}

const ITEM =
  'text-text-primary hover:bg-surface-sunken focus-visible:outline-focus-ring flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm no-underline focus-visible:outline-2'

/**
 * The switcher. Every workspace the caller can see, the active one marked; then the three doors
 * the screenshot's tooltip names — create, invite, settings.
 */
function WorkspaceMenu({
  workspaces,
  active,
  onSwitch,
  onCreate,
  onNavigate,
  canInvite,
}: {
  readonly workspaces: readonly Workspace[]
  readonly active: Workspace | null
  readonly onSwitch: (workspaceId: string) => void
  readonly onCreate: () => void
  readonly onNavigate: () => void
  /** Whether the invite door is worth offering — `mayInvite` (ADR 0196). */
  readonly canInvite: boolean
}) {
  const name = active?.name ?? 'Workspace'
  // The active team's own settings page when there is one; the organization's Teams section
  // otherwise, which is where a team comes from.
  const settingsHref =
    active === null ? '/app/admin/teams' : `/app/admin/workspaces/${active.id}/settings`

  return (
    <Dropdown
      label="Workspace"
      trigger={({ open, toggle }) => (
        <button
          type="button"
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={toggle}
          data-testid="workspace-menu"
          className="border-border-subtle bg-surface-raised hover:border-border-strong flex h-11 w-full items-center gap-2 rounded-md border px-2 text-left"
        >
          <Avatar name={name} size="sm" />
          <span className="visually-hidden">Workspace: </span>
          <span className="text-text-primary min-w-0 flex-1 truncate text-sm font-medium">
            {name}
          </span>
          <Icon name="chevron-down" size={14} className="text-text-muted" />
        </button>
      )}
    >
      {(close) => (
        <>
          <p className="text-text-muted m-0 px-2 pb-1 pt-1 text-xs font-semibold uppercase tracking-wide">
            Workspaces
          </p>
          {workspaces.length === 0 ? (
            <p className="text-text-muted m-0 px-2 py-1.5 text-sm">
              Your workspaces could not be listed just now.
            </p>
          ) : (
            workspaces.map((workspace) => {
              const current = active !== null && workspace.id === active.id
              return (
                <button
                  key={workspace.id}
                  type="button"
                  role="menuitem"
                  {...(current ? { 'aria-current': 'true' as const } : {})}
                  onClick={() => {
                    onSwitch(workspace.id)
                    close()
                  }}
                  className={ITEM}
                >
                  <Avatar name={workspace.name} size="sm" />
                  <span className="min-w-0 flex-1 truncate">{workspace.name}</span>
                  {current ? <Icon name="check" size={14} className="text-accent" /> : null}
                </button>
              )
            })
          )}
          <div className="border-border-subtle my-1.5 border-t" role="separator" />
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              close()
              onCreate()
            }}
            className={ITEM}
          >
            <Icon name="plus" size={16} className="text-text-muted" />
            Create workspace
          </button>
          {canInvite ? (
            <NavLink
              to="/app/admin/members"
              role="menuitem"
              onClick={() => {
                close()
                onNavigate()
              }}
              className={ITEM}
            >
              <Icon name="users" size={16} className="text-text-muted" />
              Invite people
            </NavLink>
          ) : null}
          <NavLink
            to={settingsHref}
            role="menuitem"
            onClick={() => {
              close()
              onNavigate()
            }}
            className={ITEM}
          >
            <Icon name="sliders" size={16} className="text-text-muted" />
            Workspace settings
          </NavLink>
        </>
      )}
    </Dropdown>
  )
}

/**
 * `+ Create`. The same four doors the Start band offers, resolved by the same function, so the
 * International composer is where a document starts from either place. A menu that offered a
 * fifth door nothing else has would be a second opinion about where sending begins.
 */
function CreateMenu({ onNavigate }: { readonly onNavigate: () => void }) {
  const to = destinations('international')
  const items: ReadonlyArray<{
    readonly to: string
    readonly label: string
    readonly hint: string
    readonly icon: Area['icon']
  }> = [
    {
      to: to.get_signatures,
      label: 'Upload a document',
      hint: 'PDF or Office. Add who signs, place the fields, send.',
      icon: 'upload',
    },
    {
      to: to.sign_only,
      label: 'Sign a document yourself',
      hint: 'You are the only signer.',
      icon: 'pen',
    },
    {
      to: '/app/templates',
      label: 'Use a template',
      hint: 'Start from a layout you have saved.',
      icon: 'template',
    },
    {
      to: '/app/templates?create=1',
      label: 'Create a template',
      hint: 'A saved envelope — documents, fields and roles — ready to reuse.',
      icon: 'plus',
    },
  ]
  return (
    <Dropdown
      label="Create"
      trigger={({ open, toggle }) => (
        <button
          type="button"
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={toggle}
          className="bg-accent text-text-on-accent hover:bg-accent-hover focus-visible:outline-focus-ring flex h-11 w-full items-center justify-center gap-2 rounded-md text-sm font-semibold focus-visible:outline-2"
        >
          <Icon name="plus" size={18} />
          Create
        </button>
      )}
    >
      {(close) => (
        <>
          {items.map((item) => (
            <NavLink
              key={item.label}
              to={item.to}
              role="menuitem"
              onClick={() => {
                close()
                onNavigate()
              }}
              className={`${ITEM} items-start`}
            >
              <Icon name={item.icon} size={16} className="text-text-muted mt-0.5" />
              <span className="flex min-w-0 flex-col">
                <span>{item.label}</span>
                <span className="text-text-muted text-xs">{item.hint}</span>
              </span>
            </NavLink>
          ))}
        </>
      )}
    </Dropdown>
  )
}

/* ------------------------------------------------------------------ create a workspace ------- */

function CreateWorkspaceDialog({
  open,
  teamsOn,
  onClose,
  onCreated,
}: {
  readonly open: boolean
  readonly teamsOn: boolean
  readonly onClose: () => void
  readonly onCreated: (workspace: Workspace) => void
}) {
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async () => {
    const trimmed = name.trim()
    if (trimmed === '') return
    setBusy(true)
    setError(null)
    try {
      // Teams first, when they are off: the route refuses a second team otherwise, and the dialog
      // has already said this is what Create does. The same field the Teams section writes.
      if (!teamsOn) {
        await api('/v1/organization', { method: 'PATCH', body: { workspaces_enabled: true } })
      }
      const created = await createWorkspace(trimmed)
      setName('')
      onCreated({ id: created.id, name: created.name, is_default: created.is_default })
    } catch (e: unknown) {
      setError(
        // The 403 keeps its own sentence — only this screen knows who may create a team. Everything
        // else goes through the helper: `POST /v1/workspaces` carries no id, so a 404 can only mean
        // the deployed Worker does not serve the route, and raw it rendered as the words "not found".
        e instanceof ApiError && e.status === 403
          ? 'Creating a workspace is limited to organization administrators and owners.'
          : describeUnlessNotDeployed(e, 'Creating a team', 'The workspace could not be created.'),
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Create workspace"
      description="A team inside your organization, with its own documents, templates and contacts."
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} disabled={busy || name.trim() === ''}>
            {busy ? 'Creating…' : 'Create'}
          </Button>
        </>
      }
    >
      <Field
        label="Name"
        value={name}
        maxLength={120}
        disabled={busy}
        onChange={(e) => setName(e.currentTarget.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !busy && name.trim() !== '') {
            e.preventDefault()
            void submit()
          }
        }}
        {...(error === null ? {} : { error })}
      />
      {teamsOn ? null : (
        <p className="text-text-muted m-0 mt-3 text-sm" role="note">
          This organization does not use teams yet. Creating a workspace turns them on — everything
          already filed stays exactly where it is.
        </p>
      )}
    </Dialog>
  )
}
